import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, openBlock as _openBlock, createBlock as _createBlock, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("ul", { class: "q-my-none" }, [
  /*#__PURE__*/_createElementVNode("li", null, " d'un nom d'utilisateur (entre 3 et 64 caractères) : il est conseillé d'indiquer soit votre prénom soit votre prénom et l'initiale de votre nom, par exemple : \"Brice L\" ou \"Brice L.\" ou \"Brice\" "),
  /*#__PURE__*/_createElementVNode("li", null, " d'une adresse email qui servira d'identifiant de connexion (exemple: toto@domaine.com), "),
  /*#__PURE__*/_createElementVNode("li", null, " d'un mot de passe de connexion (rappel des règles ci-dessous). ")
], -1 /* HOISTED */)

import { ref, computed, onBeforeUnmount, toRefs, watch } from 'vue';
import { useFocus } from '@vueuse/core';

// Stores
import { useUiStore } from '@stores/ui';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// Personal Components
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import DiagGeneric from '@components/dialog/DiagGeneric.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';
import QInputUsername from '@components/form/QInputUsername.vue';
import QInputEmail from '@components/form/QInputEmail.vue';
import QInputPassword from '@components/form/QInputPassword.vue';
import MentionsLegalesPage from '@pages/terms/MentionsLegalesPage.vue';
import CGUPage from '@pages/terms/CGUPage.vue';

// Personal libs
import { $t } from 'src/i18n/utils';
import { IConfigSecurity } from '@shared/interface/config.interface';
import {
  displayDialogError,
  displayDialogSuccess,
  displayDialogWarning,
} from '@shared/lib/notify.lib';
import signupRepository from '@data/repository/signup.repository';
import { DialogSizeEnum } from '@shared/enum/dialog-size.enum';
import { PasswordTypeEnum } from '@shared/enum/password-type.enum'

/**
 * Stores
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'DialogSignup',
  props: {
  show: { required: true, type: Boolean, default: false },
},
  emits: ['close'],
  setup(__props, { emit: __emit }) {

// VENDOR
const uiStore = useUiStore();

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * HTMLElement refs
 */
const qSignupDescription = ref<HTMLInputElement>();
const qInputUsernameRef = ref();
const qInputEmailRef = ref();
const qInputPasswordRef = ref();
const qInputPasswordConfirmRef = ref();

const { focused: qInputEmailFocused } = useFocus(qInputEmailRef);
const { focused: qInputPasswordFocused } = useFocus(qInputPasswordRef);
const { focused: qInputPasswordConfirmFocused } = useFocus(
  qInputPasswordConfirmRef,
);

/**
 * Emits
 */
const emits = __emit;

/**
 * Data
 */
const showPassword = ref<boolean>(false);
const showPasswordConfirm = ref<boolean>(false);
const showMentionsLegales = ref<boolean>(false);
const showCgu = ref<boolean>(false);

const loading = ref<boolean | undefined>(false);

const username = ref<string | undefined>(undefined);
const email = ref<string | undefined>(undefined);
const password = ref<string | undefined>(undefined);
const passwordConfirm = ref<string | undefined>(undefined);
const chkAcceptRules = ref<boolean>(false);

/**
 * Computed data
 */
const securityRules = computed<IConfigSecurity | undefined>(
  () => uiStore.config?.security,
);

/**
 * Properties
 */
const props = __props;
const { show } = toRefs(props);

/**
 * Methods
 */
function onClose() {
  emits('close');
}

function onOkClick() {
  // Forms validations
  qInputUsernameRef.value?.validate();
  qInputEmailRef.value?.validate();
  qInputPasswordRef.value?.validate();
  qInputPasswordConfirmRef.value?.validate();

  if (
    !qInputUsernameRef.value?.hasError() &&
    !qInputEmailRef.value?.hasError() &&
    !qInputPasswordRef.value?.hasError() &&
    !qInputPasswordConfirmRef.value?.hasError()
  ) {
    if (password.value != passwordConfirm.value) {
      displayDialogError({
        message:
          'Le mot de passe et sa confirmation doivent être strictement identiques',
      });
    } else if (!chkAcceptRules.value) {
      displayDialogWarning({
        message:
          "Pour continuer, vous devez accepter les mentions légales et les conditions d'utilisation",
      });
    } else {
      manageOk();
    }
  }
}

function manageOk() {
  loading.value = true;

  // Call api endpoint
  signupRepository
    .signup({
      username: username.value ?? '',
      email: email.value ?? '',
      password: password.value ?? '',
      passwordConfirm: passwordConfirm.value ?? '',
      chkAcceptRules: chkAcceptRules.value,
    })
    .then(() => {
      displayDialogSuccess({
        message:
          'Demande confirmée. Vous allez recevoir un mail vous invitant à valider votre adresse email.',
      });

      onClose();
    })
    .catch((error: Error) =>
      displayDialogError({
        message: error.message ?? 'null',
        isHtml: true,
      }),
    )
    .finally(() => (loading.value = false));
}

function onCancelClick() {
  // Forms validations
  qInputUsernameRef.value?.resetValidation();
  qInputEmailRef.value?.resetValidation();
  qInputPasswordRef.value?.resetValidation();
  qInputPasswordConfirmRef.value?.resetValidation();

  onClose();
}

function clearUsername() {
  qInputUsernameRef.value?.resetValidation();
  username.value = undefined;
  qInputEmailFocused.value = true;
}

function clearEmail() {
  qInputEmailRef.value?.resetValidation();
  email.value = undefined;
  qInputEmailFocused.value = true;
}

function clearPassword() {
  qInputPasswordRef.value?.resetValidation();
  password.value = undefined;
  qInputPasswordFocused.value = true;
}

function clearPasswordConfirm() {
  qInputPasswordConfirmRef.value?.resetValidation();
  passwordConfirm.value = undefined;
  qInputPasswordConfirmFocused.value = true;
}

/**
 * Watch
 */
watch(
  () => show.value,
  (newValue: boolean | undefined) => {
    if (newValue) {
      username.value = undefined;
      email.value = undefined;
      password.value = undefined;
      passwordConfirm.value = undefined;
      chkAcceptRules.value = false;
      showPassword.value = false;
      showPasswordConfirm.value = false;
    }
  },
);

/**
 * Hooks
 */
onBeforeUnmount(() => {
  password.value = undefined;
  passwordConfirm.value = undefined;
  loading.value = undefined;
});

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_checkbox = _resolveComponent("q-checkbox")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(QDialogTemplate, {
      "show-dialog": _unref(show),
      title: _unref($t)('signup.title'),
      "display-close-button": true,
      maximized: _unref(isMobile),
      persistent: true,
      "dialog-size": _unref(DialogSizeEnum).SMALL,
      onClose: _cache[7] || (_cache[7] = ($event: any) => (onClose()))
    }, {
      content: _withCtx(() => [
        _createVNode(_component_q_card, {
          flat: "",
          class: "q-pt-xs"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_q_card_section, {
              class: "q-pt-none",
              tabindex: "-1",
              ref_key: "qSignupDescription",
              ref: qSignupDescription
            }, {
              default: _withCtx(() => [
                _createTextVNode(" La demande de création de compte nécessite la saisie : "),
                _hoisted_1
              ]),
              _: 1 /* STABLE */
            }, 512 /* NEED_PATCH */),
            _createVNode(_component_q_card_section, { class: "q-pt-none" }, {
              default: _withCtx(() => [
                _createTextVNode(" Rappel : " + _toDisplayString(_unref($t)('form.password.rules', {
              passwordMin: securityRules.value?.password?.min || 0,
              passwordMax: securityRules.value?.password?.max || 0,
              passwordAuthCar: securityRules.value?.password?.authCar || '*-',
            })), 1 /* TEXT */)
              ]),
              _: 1 /* STABLE */
            }),
            _createVNode(_component_q_card_section, { class: "text-italic q-my-none q-py-none" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref($t)('generic.form.mandatoryFields')) + " ", 1 /* TEXT */),
                _createCommentVNode(" Tous les champs suivants sont obligatoires (y compris la case à cocher) ")
              ]),
              _: 1 /* STABLE */
            }),
            _createVNode(_component_q_card_section, { class: "q-pa-none q-pt-none" }, {
              default: _withCtx(() => [
                _createCommentVNode(" Input : username "),
                _createVNode(QInputUsername, {
                  modelValue: username.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((username).value = $event)),
                  ref_key: "qInputUsernameRef",
                  ref: qInputUsernameRef,
                  dense: true,
                  label: _unref($t)('form.username.label'),
                  "extra-class": 'q-mx-sm',
                  disable: loading.value,
                  "on-enter-key": onOkClick
                }, null, 8 /* PROPS */, ["modelValue", "label", "disable"]),
                _createCommentVNode(" Input : email "),
                _createVNode(QInputEmail, {
                  modelValue: email.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((email).value = $event)),
                  ref_key: "qInputEmailRef",
                  ref: qInputEmailRef,
                  dense: true,
                  label: _unref($t)('form.email.label'),
                  "extra-class": 'q-mx-sm',
                  disable: loading.value,
                  "on-enter-key": onOkClick
                }, null, 8 /* PROPS */, ["modelValue", "label", "disable"]),
                _createCommentVNode(" Input : password "),
                _createVNode(QInputPassword, {
                  modelValue: password.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((password).value = $event)),
                  ref_key: "qInputPasswordRef",
                  ref: qInputPasswordRef,
                  dense: true,
                  label: _unref($t)('form.password.normal.label'),
                  "extra-class": 'q-mx-sm',
                  disable: loading.value,
                  "on-enter-key": onOkClick,
                  "password-type": _unref(PasswordTypeEnum).NORMAL
                }, null, 8 /* PROPS */, ["modelValue", "label", "disable", "password-type"]),
                _createCommentVNode(" Input : password confirm "),
                _createVNode(QInputPassword, {
                  modelValue: passwordConfirm.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((passwordConfirm).value = $event)),
                  ref_key: "qInputPasswordConfirmRef",
                  ref: qInputPasswordConfirmRef,
                  dense: true,
                  label: _unref($t)('form.password.confirmation.label'),
                  "extra-class": 'q-mx-sm',
                  disable: loading.value,
                  "on-enter-key": onOkClick,
                  "password-type": _unref(PasswordTypeEnum).CONFIRMATION
                }, null, 8 /* PROPS */, ["modelValue", "label", "disable", "password-type"])
              ]),
              _: 1 /* STABLE */
            }),
            _createVNode(_component_q_card_section, { class: "" }, {
              default: _withCtx(() => [
                _createTextVNode(" Pour que la demande de création soit effective, vous devez lire et accepter les "),
                _createVNode(_component_q_btn, {
                  class: "q-py-none q-px-xs q-ma-none accept-button",
                  style: {"min-height":"0px"},
                  flat: "",
                  "no-caps": "",
                  label: "mentions légales",
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (showMentionsLegales.value = true))
                }),
                _createTextVNode(" ainsi que les "),
                _createVNode(_component_q_btn, {
                  class: "q-py-none q-px-xs q-ma-none accept-button",
                  style: {"min-height":"0px"},
                  flat: "",
                  "no-caps": "",
                  label: "conditions générales d'utilisation",
                  onClick: _cache[5] || (_cache[5] = ($event: any) => (showCgu.value = true))
                }),
                _createTextVNode(". ")
              ]),
              _: 1 /* STABLE */
            }),
            _createVNode(_component_q_card_section, { class: "q-pt-none" }, {
              default: _withCtx(() => [
                _createCommentVNode(" Signup accept "),
                _createVNode(_component_q_checkbox, {
                  class: "set-outline",
                  modelValue: chkAcceptRules.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((chkAcceptRules).value = $event)),
                  disable: loading.value,
                  label: _unref($t)('signup.accept')
                }, null, 8 /* PROPS */, ["modelValue", "disable", "label"])
              ]),
              _: 1 /* STABLE */
            })
          ]),
          _: 1 /* STABLE */
        })
      ]),
      footer: _withCtx(() => [
        _createVNode(_component_q_separator),
        _createVNode(_component_q_card_actions, { align: "right" }, {
          default: _withCtx(() => [
            (!_unref(isMobile))
              ? (_openBlock(), _createBlock(QBtnCancel, {
                  key: 0,
                  label: _unref($t)('signup.buttons.cancel.label'),
                  type: 'button',
                  onClick: onCancelClick,
                  "icon-left": 'close',
                  class: "q-mr-xs"
                }, null, 8 /* PROPS */, ["label"]))
              : _createCommentVNode("v-if", true),
            _createVNode(QBtnValid, {
              label: _unref($t)('signup.buttons.ok.label'),
              type: 'submit',
              onClick: onOkClick,
              loading: loading.value,
              disable: loading.value || !chkAcceptRules.value,
              "loading-label": _unref($t)('signin.loader.label'),
              "icon-right": chkAcceptRules.value ? 'login' : 'block'
            }, null, 8 /* PROPS */, ["label", "loading", "disable", "loading-label", "icon-right"])
          ]),
          _: 1 /* STABLE */
        })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["show-dialog", "title", "maximized", "dialog-size"]),
    _createVNode(DiagGeneric, {
      show: showMentionsLegales.value,
      title: 'Mentions légales',
      ok: { label: 'Fermer' },
      "hide-cancel": true,
      onClose: _cache[8] || (_cache[8] = ($event: any) => (showMentionsLegales.value = false)),
      onCancel: _cache[9] || (_cache[9] = ($event: any) => (showMentionsLegales.value = false)),
      onValid: _cache[10] || (_cache[10] = ($event: any) => (showMentionsLegales.value = false))
    }, {
      custom: _withCtx(() => [
        _createVNode(MentionsLegalesPage, { "display-title": false })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["show"]),
    _createVNode(DiagGeneric, {
      show: showCgu.value,
      title: 'Conditions d\'utilisation',
      ok: { label: 'Fermer' },
      "hide-cancel": true,
      onClose: _cache[11] || (_cache[11] = ($event: any) => (showCgu.value = false)),
      onCancel: _cache[12] || (_cache[12] = ($event: any) => (showCgu.value = false)),
      onValid: _cache[13] || (_cache[13] = ($event: any) => (showCgu.value = false)),
      class: "dialog-container"
    }, {
      custom: _withCtx(() => [
        _createVNode(CGUPage, { "display-title": false })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["show"])
  ], 64 /* STABLE_FRAGMENT */))
}
}

})